import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import ReactJson from "react-json-view";
import { fetchJvClaims, fetchJvEmployees, fetchJvTransactionsByMemberUuid, fetchMemberByEmployeeId, fetchMemberByMemberUuid, fetchMemberBySystemIdentifier } from "../../utils/apiUtils";
import ToggleSwitch from "../shared/ToggleSwitch";
import { useInView } from "react-intersection-observer";
import SharedTable from "../benefitPlan/SharedTable";
import SimpleTable from "../shared/SimpleTable";
import NotificationTable from "../dynamodb/NotificationTable";
import EmployeeStopLoss from "./EmployeeStopLoss";
import { asJvArray } from "../benefitPlan/SharedTable/detailViews/StopLossContractDetail";

function getAge(dateString) {
    const today = new Date();
    const birthDate = new Date(dateString); // Create a Date object from the input string

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    // If the current month is before the birth month, or if it's the same month 
    // but the current day is before the birth day, the person hasn't had their 
    // birthday yet this year, so subtract a year from the age.
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
}

const Employee = () => {
    const params = useParams();
    const navigate = useNavigate();

    const certificateNumber = params.certificateNumber;
    const depSequenceNumber = params.DepSequenceNumber
    const activeTab = params.activeTab || "details";

    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
    const [selectedDepSeqNum, setSelectedDepSeqNum] = useState(depSequenceNumber)

    const {
        isPending,
        error: employeeError,
        data: employeeData,
        status,
        isError,
        isLoading,
        isFetching,
    } = useQuery({
        queryKey: ["employeeByCert", certificateNumber],
        queryFn: async () => {
            const result = await fetchJvEmployees({ certificateNumber: certificateNumber })
            return result
        },
        select: (data) => data?.Response.MemberDetails,
        enabled: !!certificateNumber,
        placeholderData: keepPreviousData,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: 'always',
    });

    const selectedFamilyMember = employeeData?.find(e => e.DepSequenceNumber == selectedDepSeqNum);

    const limit = 10;
    const { data: dynamDBMembersData } = useQuery({
        queryKey: ["fetchMemberBySystemIdentifier", limit, selectedFamilyMember],
        queryFn: async ({ }) => {


            let result = await fetchMemberBySystemIdentifier({ systemIdentifier: `${selectedFamilyMember?.CertificateNumber}#${String(selectedFamilyMember?.DepSequenceNumber)}` });
            if (result.Count) {
                const tasks = result.Items.map(item => fetchMemberByMemberUuid({ memberUuid: item.memberUuid }))
                result = await Promise.all(tasks)
            }

            return result
        },
        select: (data) => data?.map(d => d.Items).flat(),
        enabled: !!selectedFamilyMember,
        placeholderData: keepPreviousData,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
    });

    const { data: jvEmployeeDetails } = useQuery({
        queryKey: ["fetchMemberByEmployeeId", limit, selectedFamilyMember],
        queryFn: async ({ }) => {


            let result = await fetchMemberByEmployeeId({
                employeeid: selectedFamilyMember.EmployeeID,
                DepSequenceNumber: selectedFamilyMember.DepSequenceNumber
            });


            return result
        },
        select: (data) => data?.Response.Member,
        enabled: !!selectedFamilyMember,
        placeholderData: keepPreviousData,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
    });

    const memberUuid = dynamDBMembersData?.[0]?.memberUuid;
    const employeeId = dynamDBMembersData?.[0]?.employeeId;
    const jvMember = asJvArray(jvEmployeeDetails)[0];


    return (
        <div className={"container "}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                    <p className="title">Employee</p>
                    <p className="sub-title">Cert: {certificateNumber} | memberUuid: {memberUuid}</p>
                </div>

                <div>
                    <ToggleSwitch label={`Auto Refresh`} onToggle={(val) => setAutoRefreshEnabled(val)} initialState={autoRefreshEnabled} />
                </div>
            </div>

            {status === "loading" || (isFetching || isLoading) ? <div style={{ padding: "1rem" }}> <div className="loader" /> </div> : status === "error" || isError ? <span className="danger">Error: {employeeError.message}</span> : <></>}
            <br />

            {employeeData && (<>
                <SimpleTable
                    onClickHandler={(row) => setSelectedDepSeqNum(row.DepSequenceNumber)}
                    selectedIndex={selectedDepSeqNum}
                    disableHeaderFormatting={true}
                    //'CertificateNumber', "EmployeeID", "EmployeeFirstName", "EmployeeLastName", "DependentID",
                    columns={["EmployeeID", 'DependentID', "DepSequenceNumber", "PatientFirstName", "PatientLastName", "Relationship", 'BirthDate', 'age'].map(
                        (k) => ({ label: k, attribute: k })
                    )}
                    rows={employeeData?.sort((a, b) => a.DepSequenceNumber - b.DepSequenceNumber).map(e => ({
                        ...e,
                        age: getAge(e.BirthDate)
                    }))}
                />

                {selectedFamilyMember && <ReactJson
                    style={{ padding: "1rem" }}
                    src={selectedFamilyMember}
                    name={false}
                    collapsed={true}
                    collapseStringsAfterLength={150}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    sortKeys={false}
                />}

                <div className="tabs">
                    {["details", "stop-loss", "accum", "authorization", "claims", "transactions", 'notifications'].map((tab) => (
                        <div className={"tab" + (activeTab === tab ? " active-tab" : "")} key={tab} onClick={() => navigate(`/jv/employees/${certificateNumber}/${selectedDepSeqNum}/${tab}`)}>
                            {tab}
                        </div>
                    ))}
                </div>


                {activeTab === "details" ? (<div className="scrollable" style={{ flex: 1, minHeight: 0 }}>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        {dynamDBMembersData &&
                            <div>
                                <p style={{ fontSize: "1.2rem", fontWeight: "600", padding: "1rem 0rem 0rem 0rem" }}>DynamoDB</p>
                                {dynamDBMembersData?.length > 1 && <p className="danger">Duplicate member in dynamoDB</p>}
                                <ReactJson
                                    style={{ padding: "1rem" }}
                                    src={dynamDBMembersData}
                                    name={false}
                                    collapsed={2}
                                    collapseStringsAfterLength={150}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                    sortKeys={false}
                                />
                            </div>
                        }
                        {jvEmployeeDetails &&
                            <div>
                                <p style={{ fontSize: "1.2rem", fontWeight: "600", padding: "1rem 0rem 0rem 0rem" }}>JV</p>
                                <ReactJson
                                    style={{ padding: "1rem" }}
                                    src={jvEmployeeDetails}
                                    name={false}
                                    collapsed={3}
                                    collapseStringsAfterLength={150}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                    sortKeys={false}
                                />
                            </div>
                        }
                    </div>



                </div>) : activeTab === "stop-loss" ? (
                    <div className="scrollable" style={{ paddingTop: '1rem', paddingBottom: '2rem', flex: 1, minHeight: 0 }}>
                        <EmployeeStopLoss member={jvMember} searchRow={selectedFamilyMember} />
                    </div>
                ) : activeTab === "transactions" ? (
                    <Transactions memberUuid={memberUuid} autoRefreshEnabled={autoRefreshEnabled} />
                ) : activeTab === 'claims' ? <Claims selectedFamilyMember={selectedFamilyMember} /> : activeTab === 'notifications' ? <NotificationTable memberUuid={memberUuid} /> : <div></div>}

            </>)}


        </div>
    )
}

export default Employee

const Transactions = ({ memberUuid, autoRefreshEnabled }) => {
    const { ref, inView } = useInView();

    const queryKey = ['jv', "transactions", memberUuid];

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading, //first time only
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error,
    } = useInfiniteQuery({
        retry: false,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = { patientMemberUuid: memberUuid, lastEvaluatedKey: pageParam };

            return fetchJvTransactionsByMemberUuid(params);
        },
        select: (data) => {
            return {
                pages: data.pages.flatMap((x) => x.Items),
                pageParams: data.pageParams,
            };
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null;
        },
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: 'always',
        staleTime: 0, //,Infinity,
        enabled: !!memberUuid, //&& !!showChat
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const items = data?.pages.flat();
    const columns = ["transactionId", "transactionType", "patientFirstName", "patientLastName", 'transationDate', "status"];

    return (
        <>
            <SharedTable
                type={"jv-transactions"}
                columns={columns}
                items={items}
                activeDraftId={null}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                isFetching={isFetching}
                isLoading={isLoading}
                // onDetailHandler={onDetailHandler}
                ref={ref}
            />

        </>

    );
};

const Claims = ({ selectedFamilyMember, employeeId, depSequenceNumber, autoRefreshEnabled }) => {
    const { ref, inView } = useInView();

    const queryKey = ['jv', "claims", selectedFamilyMember?.EmployeeID, selectedFamilyMember?.DepSequenceNumber];

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading, //first time only
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error,
    } = useInfiniteQuery({
        retry: false,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = pageParam || {
                patientId: selectedFamilyMember?.DependentID || selectedFamilyMember?.EmployeeID,
                depSequenceNumber: selectedFamilyMember?.DepSequenceNumber
            };

            // if (depSequenceNumber) {
            //     params.depSequenceNumber = depSequenceNumber
            // }

            return fetchJvClaims(params);
        },
        select: (data) => {
            //   console.log({ data });
            return {
                //  count: data.pages?.[0]?.MetaData.RecordCount,
                rows: data.pages.map((page) => page.Response.AdjudicationResult).flatMap((x) => x).filter(x => Object.keys(x)?.length),

            };
        },
        getNextPageParam: (lastPage, pages) => {
            //    console.log({ lastPage })
            const nextPageUrl = lastPage?.Response?.Pagination?.Next

            if (!nextPageUrl) return undefined;
            return {
                nextPageUrl: nextPageUrl.split('/rest/')[1]
            }
        },
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: 'always',
        staleTime: 0, //,Infinity,
        enabled: !!selectedFamilyMember?.EmployeeID, //&& !!showChat
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const items = data?.rows
    const columns = ["Claimnumber", 'PrimaryICD', "Product", 'Providerofficename', 'Providertaxid', "IncurredDate", "ReceivedDate", "TotCharge", 'PaymentDate', "TotPaid"];

    return (
        <>
            {/* <ReactJson
                style={{ padding: "1rem" }}
                src={selectedFamilyMember}
                name={false}
                collapsed={true}
                collapseStringsAfterLength={150}
                displayDataTypes={false}
                displayObjectSize={false}
                sortKeys={false}
            /> */}
            <SharedTable
                type={"jv-claims"}
                columns={columns}
                items={items}
                activeDraftId={null}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                isFetching={isFetching}
                isLoading={isLoading}
                // onDetailHandler={onDetailHandler}
                ref={ref}
            />

        </>

    );
};

